/**
 * Plain-data projectile fired by a weapon. Pooled by ProjectilePool rather
 * than constructed per-shot — auto-fire weapons can spawn dozens of these
 * a second and we want zero GC churn during a fight.
 *
 * Hit model: `hitsRemaining` counts down each time it deals damage to the
 * boss (the only possible target — there's no "pierce to the next enemy"
 * in a boss-only fight, unlike the horde-clearers this genre usually
 * means "pierce" in), and `hitCooldownDuration` gates how often it can
 * re-hit while still overlapping the boss, so a persistent hazard (Orbit
 * Ward's shards circling through the boss) ticks damage instead of
 * melting it in one frame. Normal bullets are `hits: 1` — deactivate on
 * their one hit — same as before this was generalized.
 *
 * Two movement modes: straight-line (default, driven by vx/vy) or orbit
 * (if `orbitCenter` is set, position is computed by circling that point —
 * used by Orbit Ward so shards track the player without the weapon having
 * to re-fire every frame) — plus optional homing (limited turn-rate
 * steering toward `target`, mirroring BossBullet's homing shards) for
 * Arc Needle.
 */
export class Projectile {
  constructor() {
    this.active = false;
    this.reset();
  }

  reset() {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.speed = 0;
    this.radius = 2;
    this.damage = 0;
    this.hitsRemaining = 1;
    this.hitCooldown = 0;
    this.hitCooldownDuration = 0.15;
    this.lifetime = 0; // seconds remaining before despawn
    this.maxLifetime = 0; // lifetime at spawn — lets renderers compute a 0->1 age ratio (e.g. Nova Pulse's expanding ring)
    this.trail = null;
    this.prevX = 0;
    this.prevY = 0;
    this.color = '#f4e04d';
    this.style = null; // null = filled dot; 'ring' = stroked outline (Nova Pulse)
    this.blocksBullets = false; // true = destroys boss bullets it overlaps (Orbit Ward shards)
    this.knocksBack = false; // true = shoves struck minions away on hit (Orbit Ward shards; never applies to the boss)
    this.knockbackForce = 0;
    this.appliesDot = false; // true = tags whatever it hits with a stacking damage-over-time (Orbit Ward lvl4+, see CombatSystem)
    this.dotDamagePerTick = 0;

    this.homing = false;
    this.turnRate = 0;
    this.target = null;

    this.orbitCenter = null;
    this.orbitRadius = 0;
    this.orbitAngle = 0;
    this.orbitAngularSpeed = 0;
  }

  spawn({
    x, y, vx = 0, vy = 0, radius, damage, hits = 1, hitCooldownDuration = 0.15, lifetime = 2, color, style = null,
    blocksBullets = false,
    knocksBack = false, knockbackForce = 0,
    appliesDot = false, dotDamagePerTick = 0,
    homing = false, turnRate = 0, target = null,
    orbitCenter = null, orbitRadius = 0, orbitAngle = 0, orbitAngularSpeed = 0,
  }) {
    this.active = true;
    this.x = x;
    this.y = y;
    this.vx = vx;
    this.vy = vy;
    this.speed = Math.hypot(vx, vy);
    this.radius = radius;
    this.damage = damage;
    this.hitsRemaining = hits;
    this.hitCooldown = 0;
    this.hitCooldownDuration = hitCooldownDuration;
    this.lifetime = lifetime;
    this.maxLifetime = lifetime;
    this.color = color ?? this.color;
    this.trail = null;
    this.prevX = x;
    this.prevY = y;
    this.style = style;
    this.blocksBullets = blocksBullets;
    this.knocksBack = knocksBack;
    this.knockbackForce = knockbackForce;
    this.appliesDot = appliesDot;
    this.dotDamagePerTick = dotDamagePerTick;
    this.homing = homing;
    this.turnRate = turnRate;
    this.target = target;
    this.orbitCenter = orbitCenter;
    this.orbitRadius = orbitRadius;
    this.orbitAngle = orbitAngle;
    this.orbitAngularSpeed = orbitAngularSpeed;
    return this;
  }

  update(dt) {
    this.prevX = this.x;
    this.prevY = this.y;
    if (this.orbitCenter) {
      this.orbitAngle += this.orbitAngularSpeed * dt;
      this.x = this.orbitCenter.x + Math.cos(this.orbitAngle) * this.orbitRadius;
      this.y = this.orbitCenter.y + Math.sin(this.orbitAngle) * this.orbitRadius;
    } else {
      if (this.homing && this.target) this._steerToward(this.target, dt);
      this.x += this.vx * dt;
      this.y += this.vy * dt;
    }

    if (this.hitCooldown > 0) this.hitCooldown -= dt;
    this.lifetime -= dt;
    if (this.lifetime <= 0) this.active = false;
  }

  _steerToward(target, dt) {
    const desired = Math.atan2(target.y - this.y, target.x - this.x);
    const current = Math.atan2(this.vy, this.vx);
    let diff = desired - current;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const maxTurn = this.turnRate * dt;
    const turn = Math.max(-maxTurn, Math.min(maxTurn, diff));
    const newAngle = current + turn;
    this.vx = Math.cos(newAngle) * this.speed;
    this.vy = Math.sin(newAngle) * this.speed;
  }
}
